/**
 * The local database.
 *
 * Four stores, and the reason for each is worth stating because the temptation is always to add
 * a fifth:
 *
 * - **`maps`** — one row per map, holding its metadata. This is the library listing, and it is
 *   deliberately *not* the document: opening a map to render a row in a list is how a local
 *   library becomes slow at exactly the size where it starts to matter.
 * - **`journal`** — the append-only record of every edit. The compound `[mapId+sequence]` primary
 *   key is what makes "the entries after this one" an index range rather than a scan, and it is
 *   what makes a reused sequence a constraint violation rather than a silent overwrite.
 * - **`checkpoints`** — one row per map, the document as of a sequence. Replaying from here is
 *   what bounds recovery time however long the session ran.
 * - **`assets`** — declared now, written from step 21. A table added later costs a version bump
 *   and an upgrade path for every user's database; a table declared early costs nothing.
 *
 * Version 1. Every later change to this schema is a new `.version()` block with an upgrade
 * function, never an edit to this one — Dexie replays the whole chain on a database created by an
 * older build, and rewriting history here means those databases upgrade to something that never
 * existed.
 */
import Dexie, {} from 'dexie';
export const DATABASE_NAME = 'orbit-mind';
export const DATABASE_VERSION = 4;
/**
 * Opens the database.
 *
 * `name` is a parameter so tests get an isolated database per case, and so a future preview or
 * multi-account build can keep two of them side by side without inventing a prefix convention.
 */
export function createDatabase(name = DATABASE_NAME) {
    const db = new Dexie(name);
    db.version(1).stores({
        maps: 'mapId, updatedAt, deletedAt',
        // The compound primary key is the uniqueness guarantee for a sequence, and `[mapId+synced]`
        // is the outbox: unsynced entries for one map, in sequence order, without reading the rest.
        journal: '[mapId+sequence], mapId, [mapId+synced], [mapId+operationId]',
        checkpoints: 'mapId',
        assets: 'storageKey, mapId',
    });
    // Version 2 adds attachment metadata. A new block rather than an edit to version 1: Dexie
    // replays the whole chain on a database created by an older build, so rewriting history here
    // would upgrade those databases to a schema that never existed.
    db.version(2).stores({
        attachments: 'attachmentId, mapId, storageKey, [mapId+nodeId]',
    });
    db.version(3).stores({ lifecycle: 'mapId, pending' });
    db.version(4).stores({ settings: 'key' });
    return db;
}
//# sourceMappingURL=database.js.map